import { computed, ref, watch } from 'vue';
import { useAppModeStore } from 'src/stores/appMode';
import { tap, filter, debounceTime } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
/**
 * Plugin to scroll to position offset when
 * keyboard is shown
 * @param scrollLogic boolean to show if we are allowed to scroll.
 *    usually we will scroll on the frontmost dialog, then when no
 *    no dialogs are opened, then we scroll the page
 * @param scrollToFunc scroll to function
 * @param scrollToFuncWithCondition function to scroll to position
 *  when additional checks for keyboard offsets
 * @returns {
 *  touchDetect: (e: VueTouchEvt) => void,
 *  touchMove: () => void
 * }
 */
const KeyboardShowScrollFunctions = (scrollLogic, scrollToFunc, scrollToFuncWithCondition) => {
    const appModeStore = useAppModeStore();
    const keyboardHtVal = computed(() => appModeStore.keyboardHtVal);
    const savedTouchEleId = ref(null);
    const savedTouchEle = ref(null);
    const savedTouchY = ref(0);
    const userScrolling = ref(false);
    /**
     * Function to decide where to scroll to
     * @param keyboardHeight height of keyboard popup
     * @returns void
     */
    function scrollToEleFunc(keyboardHeight) {
        /**
         * if keyboard is not shown, skip
         */
        if (!keyboardHeight || !scrollLogic.value)
            return;
        /**
         * if touch element is specified, scroll to touched element
         */
        if (savedTouchEle.value || savedTouchEleId.value) {
            let touchElement = null;
            if ((typeof document !== 'undefined') && savedTouchEleId.value) {
                touchElement = document.getElementById(savedTouchEleId.value);
            }
            if (!touchElement) {
                touchElement = savedTouchEle.value;
            }
            delayScroll$.next(touchElement);
        }
        else if (savedTouchY.value) {
            /**
             * Scroll to touched position
             */
            scrollToFunc(savedTouchY.value);
        }
        else {
            /**
             * Scroll to position after taking keyboard ht into account
             */
            scrollToFuncWithCondition(keyboardHeight);
        }
    }
    /**
     * When user touches the screen, save the element and y position
     * @param e VueTouchEvt
     * @returns void
     */
    function touchDetect(e) {
        // ignore event if its a scroll
        if (userScrolling.value) {
            userScrolling.value = false;
            return;
        }
        /**
         * Save element from evt
         */
        savedTouchEleId.value = e.target?.id;
        savedTouchEle.value = e.target;
        savedTouchY.value = e.pageY;
        /**
         * scroll to touched element if keyboard is shown.
         * only scroll to input/textarea
         */
        if (keyboardHtVal.value && (['input', 'textarea'].indexOf((e.target.tagName || '').toLowerCase()) > -1)) {
            scrollToEleFunc(keyboardHtVal.value);
        }
    }
    /**
     * When user is scrolling
     */
    function touchMove() {
        userScrolling.value = true;
    }
    /**
     * scroll to element
     */
    const { subject: delayScroll$ } = rxInitSubjectCallback();
    useObservable(delayScroll$.pipe(filter((v) => v !== null), debounceTime(200), tap((touchElement) => {
        touchElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        });
    })));
    watch(keyboardHtVal, (newValue) => {
        scrollToEleFunc(newValue);
    });
    return {
        touchDetect,
        touchMove
    };
};
export { KeyboardShowScrollFunctions };
